/**
 * v3.20-1502 · Paste Client Information card.
 *
 * Lives inside the existing Data Import & Cleanup Center as a new
 * card BELOW "Paste from Spreadsheet".  Runs a two-pass extraction on
 * the backend (regex first, Emergent Claude Sonnet fallback), renders
 * a review screen with per-field confidence pills, blocks the import
 * on an SSN duplicate, and finally imports through the SAME
 * ClientsService.create_client pipeline used by the manual New Client
 * form (ownership, workflow, ghost, timeline, audit).
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import {
  ClipboardPaste, Sparkles, Loader2, Eye, Upload, X, AlertTriangle,
  Bot, ShieldCheck, CircleHelp,
} from 'lucide-react';

const FIELD_LABELS = {
  full_name:      'Full Name',
  first_name:     'First Name',
  last_name:      'Last Name',
  email:          'Email',
  phone:          'Phone',
  address:        'Address',
  city:           'City',
  state:          'State',
  zip:            'Zip',
  ssn:            'SSN',
  dob:            'Date of Birth',
  income:         'Annual Income',
  employment:     'Employment',
  residence:      'Residence',
  loan_amount:    'Loan Amount',
  loan_purpose:   'Loan Purpose',
  company_hint:   'Rep Company',
  application_id: 'Application ID',
  notes:          'Notes',
};

const FIELD_ORDER = [
  'application_id', 'full_name', 'first_name', 'last_name',
  'email', 'phone', 'ssn', 'dob',
  'address', 'city', 'state', 'zip',
  'income', 'employment', 'residence',
  'loan_amount', 'loan_purpose', 'company_hint',
  'notes',
];

function SourcePill({ source, confidence, generated }) {
  const label = generated ? 'Generated'
    : source === 'regex' ? 'Regex'
    : source === 'ai'    ? 'AI'
    : source === 'manual' ? 'Manual'
    : 'Missing';
  const Icon = generated ? Sparkles
    : source === 'regex' ? ShieldCheck
    : source === 'ai'    ? Bot
    : CircleHelp;
  const color = generated ? 'bg-blue-100 text-blue-700'
    : source === 'regex' ? 'bg-emerald-100 text-emerald-700'
    : source === 'ai'    ? 'bg-purple-100 text-purple-700'
    : source === 'manual' ? 'bg-slate-100 text-slate-700'
    : 'bg-amber-100 text-amber-700';
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${color}`}>
      <Icon className="w-2.5 h-2.5" />
      {label}{typeof confidence === 'number' && source !== 'manual' && !generated
        ? ` · ${Math.round(confidence * 100)}%` : ''}
    </span>
  );
}

export default function PasteClientInformationCard() {
  const [text, setText]              = useState('');
  const [preview, setPreview]        = useState(null); // { fields, duplicates, audit_id, ownership_*... }
  const [editedFields, setEdited]    = useState({});   // {field: value}
  // v3.21-1704 — ownership review state
  const [ownEdits, setOwnEdits]      = useState({});   // ownership {field: value}
  const [ownNotes, setOwnNotes]      = useState('');
  const [createOwnership, setCreateOwnership] = useState(true);
  const [busy, setBusy]              = useState(false);
  const [importing, setImporting]    = useState(false);
  const [result, setResult]          = useState(null);

  const runParse = async () => {
    if (!text.trim()) { toast.error('Paste something first'); return; }
    setBusy(true); setPreview(null); setResult(null); setEdited({});
    setOwnEdits({}); setOwnNotes(''); setCreateOwnership(true);
    try {
      const { data } = await api.post('/paste-intake/preview', { text });
      setPreview(data);
      const seed = {};
      Object.entries(data.fields || {}).forEach(([k, v]) => { seed[k] = v.value ?? ''; });
      setEdited(seed);
      // v3.21-1704 — seed ownership editor
      const ownSeed = {};
      Object.entries(data.ownership_fields || {}).forEach(([k, v]) => { ownSeed[k] = v.value ?? ''; });
      setOwnEdits(ownSeed);
      setOwnNotes(data.ownership_notes || '');
      setCreateOwnership(!!data.create_ownership_record);
      const regexCount = Object.values(data.fields || {}).filter(v => v.source === 'regex').length;
      const aiCount    = Object.values(data.fields || {}).filter(v => v.source === 'ai').length;
      const ownCount   = Object.keys(data.ownership_fields || {}).length;
      toast.success(`Parsed ${regexCount} regex + ${aiCount} AI${ownCount ? ` + ${ownCount} ownership` : ''}${data.blocking_duplicate ? ' — SSN match blocks import' : ''}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Parse failed');
    } finally { setBusy(false); }
  };

  const doImport = async (decision = null, target = null) => {
    if (!preview) return;
    if (preview.blocking_duplicate && !decision) {
      toast.error('SSN duplicate — pick Create New / Update / Merge / Cancel');
      return;
    }
    const finalFields = {};
    Object.entries(preview.fields).forEach(([k, meta]) => {
      const editedVal = editedFields[k];
      if (editedVal === '' || editedVal === undefined || editedVal === null) return;
      if (editedVal !== meta.value) {
        finalFields[k] = { ...meta, value: editedVal, source: 'manual', confidence: 1.0 };
      } else {
        finalFields[k] = meta;
      }
    });
    // v3.21-1704 — assemble ownership payload
    const ownershipFieldsFlat = {};
    Object.entries(ownEdits).forEach(([k, v]) => {
      if (v !== '' && v !== undefined && v !== null) ownershipFieldsFlat[k] = v;
    });
    setImporting(true);
    try {
      const { data } = await api.post('/paste-intake/import', {
        audit_id:            preview.audit_id,
        text,
        fields:              finalFields,
        duplicate_decision:  decision,
        duplicate_client_id: target,
        create_ownership:    createOwnership && preview.ownership_detected,
        ownership_fields:    ownershipFieldsFlat,
        ownership_notes:     ownNotes,
      });
      setResult(data);
      const ownMsg = data.ownership?.created ? ' + ownership created'
                   : data.ownership?.updated ? ' + ownership updated'
                   : '';
      toast.success(`Client imported → ${data.company_name || 'company'} (App ${data.application_id || 'auto-gen'})${ownMsg}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import failed');
    } finally { setImporting(false); }
  };

  const clearAll = () => {
    setText(''); setPreview(null); setEdited({}); setResult(null);
    setOwnEdits({}); setOwnNotes(''); setCreateOwnership(true);
  };

  const ssnDup = (preview?.duplicates || []).find(d => d.severity === 'blocking');
  const warnDups = (preview?.duplicates || []).filter(d => d.severity !== 'blocking');

  return (
    <Card className="border-2 border-purple-200 shadow-md" data-testid="paste-client-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <ClipboardPaste className="w-5 h-5 text-purple-600" />
          Paste Client Information
          <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-purple-100 text-purple-700 px-2 py-0.5 text-[10px] font-semibold">
            <Sparkles className="w-2.5 h-2.5" /> v3.20-1502
          </span>
        </CardTitle>
        <p className="text-sm text-slate-500 mt-1">
          Paste unstructured client information (emails, CRM exports, loan
          applications, notes).  Deterministic regex extracts every
          confidently-parsed field; Emergent Claude Sonnet fills the gaps.
          Every value is reviewable before import.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <textarea
          data-testid="paste-client-textarea"
          className="w-full h-56 border-2 border-dashed border-slate-300 rounded-xl p-4 font-mono text-xs bg-slate-50 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 placeholder:text-slate-400 transition-colors"
          placeholder={"Paste client information here...\n\nExample:\nfullname: Marlene Kohler\nemail: mkohler6937@att.net\nphone: 8056434403\naddress: 6937 Foothill Rd\ncity: Ventura\nstate: CA\nzip: 93003\nssn: 546-47-8967\ndob: 1962-09-27\nincome: 175000\nloanamount: 24000\npurpose: Timeshare exit"}
          value={text}
          onChange={(e) => { setText(e.target.value); setPreview(null); setResult(null); }}
        />

        <div className="flex gap-3">
          <Button
            onClick={runParse}
            disabled={busy || !text.trim()}
            className="bg-purple-600 hover:bg-purple-700 text-white"
            data-testid="paste-client-parse-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
            {busy ? 'Parsing…' : 'Parse & Review'}
          </Button>
          {text.trim() && (
            <Button variant="ghost" size="sm" onClick={clearAll} data-testid="paste-client-clear-btn">
              <X className="w-4 h-4 mr-1" /> Clear
            </Button>
          )}
        </div>

        {preview && (
          <div className="space-y-3 border-2 border-purple-100 rounded-lg p-4 bg-purple-50/40" data-testid="paste-client-review">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold text-purple-900">Import Review</div>
              <div className="text-[11px] text-slate-500">
                {preview.regex_field_count} regex · {preview.ai_field_count} AI · audit
                {' '}<code className="bg-white px-1">{preview.audit_id?.slice(0, 8)}</code>
              </div>
            </div>

            {ssnDup && (
              <div className="rounded-lg border-2 border-red-300 bg-red-50 p-3" data-testid="paste-client-ssn-block">
                <div className="flex items-center gap-2 text-red-800 font-semibold">
                  <AlertTriangle className="w-4 h-4" /> Blocking duplicate — SSN match
                </div>
                <p className="text-xs text-red-800 mt-1">
                  SSN matches an existing client — <b>{ssnDup.full_name || `${ssnDup.first_name} ${ssnDup.last_name}`}</b>
                  {' '}(App ID <code>{ssnDup.application_id || '—'}</code>, company <b>{ssnDup.company_name || '—'}</b>).
                  Select one action:
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  <Button size="sm" variant="outline" onClick={() => doImport('update_existing', ssnDup.client_id)} disabled={importing} data-testid="paste-client-update-existing-btn">Update Existing</Button>
                  <Button size="sm" variant="outline" onClick={() => doImport('merge', ssnDup.client_id)} disabled={importing} data-testid="paste-client-merge-btn">Merge</Button>
                  <Button size="sm" variant="destructive" onClick={() => doImport('create_new')} disabled={importing} data-testid="paste-client-create-new-btn">Create New</Button>
                  <Button size="sm" variant="ghost" onClick={clearAll} data-testid="paste-client-cancel-btn">Cancel</Button>
                </div>
              </div>
            )}

            {warnDups.length > 0 && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
                <div className="font-semibold mb-1">Possible duplicates ({warnDups.length}):</div>
                <ul className="list-disc pl-5">
                  {warnDups.map((d, i) => (
                    <li key={i}>
                      <b>{d.full_name || `${d.first_name} ${d.last_name}`}</b> — matched on <code>{d.kind}</code>
                      {d.application_id && <> · App <code>{d.application_id}</code></>}
                      {d.email && <> · {d.email}</>}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {FIELD_ORDER.filter(f => preview.fields[f]).map((f) => {
                const meta = preview.fields[f];
                return (
                  <div key={f} className="bg-white rounded border border-slate-200 p-2 flex flex-col gap-1" data-testid={`paste-client-field-${f}`}>
                    <div className="flex items-center justify-between">
                      <label className="text-[11px] uppercase tracking-wider text-slate-500">{FIELD_LABELS[f] || f}</label>
                      <SourcePill source={meta.source} confidence={meta.confidence} generated={meta.generated} />
                    </div>
                    <input
                      className="w-full border-b border-slate-200 focus:border-purple-500 outline-none text-sm"
                      value={editedFields[f] ?? meta.value ?? ''}
                      onChange={(e) => setEdited(s => ({ ...s, [f]: e.target.value }))}
                      data-testid={`paste-client-input-${f}`}
                    />
                  </div>
                );
              })}
            </div>

            {/* v3.21-1704 — Timeshare Ownership Detected */}
            {preview.ownership_detected && (
              <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="paste-client-ownership-section">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-amber-900">
                    Timeshare Ownership Detected
                    <span className="ml-2 text-[10px] font-normal">Existing Owner: <b>YES</b></span>
                  </div>
                  <label className="text-[11px] text-amber-900 flex items-center gap-1">
                    <input type="checkbox" checked={createOwnership}
                      onChange={(e) => setCreateOwnership(e.target.checked)}
                      data-testid="paste-client-create-ownership-toggle" />
                    Create Ownership Information Record
                  </label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {Object.entries(preview.ownership_fields || {}).map(([k, meta]) => (
                    <div key={k} className="bg-white rounded border border-amber-200 p-2 flex flex-col gap-1" data-testid={`paste-ownership-field-${k}`}>
                      <label className="text-[10px] uppercase tracking-wider text-slate-500">{k.replace(/_/g,' ')}</label>
                      <input
                        className="w-full border-b border-slate-200 focus:border-amber-500 outline-none text-sm"
                        value={ownEdits[k] ?? meta.value ?? ''}
                        onChange={(e) => setOwnEdits(s => ({ ...s, [k]: e.target.value }))}
                        data-testid={`paste-ownership-input-${k}`}
                      />
                    </div>
                  ))}
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-amber-900 block mb-1">
                    Ownership Notes / Unmapped Information
                  </label>
                  <textarea rows={6}
                    className="w-full rounded border border-amber-300 p-2 text-xs font-mono whitespace-pre-wrap bg-white"
                    value={ownNotes}
                    onChange={(e) => setOwnNotes(e.target.value)}
                    data-testid="paste-ownership-notes"
                    placeholder="Uncertain or unmapped values are preserved here."/>
                  <div className="text-[10px] text-amber-800 mt-0.5">
                    Anything not confidently mapped stays here — legacy financing terms
                    (Amount Financed, Term, Interest Rate, Monthly Payment) are preserved verbatim.
                  </div>
                </div>
              </div>
            )}

            {!ssnDup && (
              <div className="flex gap-3 pt-2">
                <Button
                  onClick={() => doImport()}
                  disabled={importing}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  data-testid="paste-client-import-btn"
                >
                  {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  {importing ? 'Importing…' : 'Import Client'}
                </Button>
                <Button variant="ghost" onClick={clearAll} data-testid="paste-client-review-cancel-btn">
                  <X className="w-4 h-4 mr-1" /> Cancel
                </Button>
              </div>
            )}

            {result && (
              <div
                className="rounded-lg border-2 border-emerald-200 bg-emerald-50 p-3 text-sm"
                data-testid="paste-client-result"
              >
                <div className="font-semibold text-emerald-900">
                  ✅ Client {result.action === 'create_new' ? 'created' : result.action}
                  {result.ownership?.created && ' + Ownership created'}
                  {result.ownership?.updated && ' + Ownership updated'}
                </div>
                <div className="text-xs text-emerald-900 mt-1">
                  ID: <code>{result.client_id}</code> · App ID: <code>{result.application_id || '—'}</code>
                  {result.company_name && <> · Company: <b>{result.company_name}</b></>}
                </div>
                <div className="flex gap-2 mt-2">
                  <a href={`/clients/${result.client_id}`}
                     className="rounded border border-emerald-500 bg-white px-2 py-1 text-[11px] text-emerald-800 hover:bg-emerald-100"
                     data-testid="paste-client-open-client-btn">Open Client</a>
                  {result.ownership?.ownership_id && (
                    <a href={`/clients/${result.client_id}?tab=ownership`}
                       className="rounded border border-emerald-500 bg-white px-2 py-1 text-[11px] text-emerald-800 hover:bg-emerald-100"
                       data-testid="paste-client-open-ownership-btn">Open Ownership Information</a>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
